import React, { useState } from "react";
import { Card, Empty, ErrorState, Kpi, PageHeading, Pill, Rate, RowsSkeleton, Table } from "../ui";
import { BarList, DonutBreakdown, SegmentBar } from "../charts";
import { useFilters } from "../filters";
import { useView } from "../data";
import { FAILURE_LABELS, OUTCOMES, PROBLEM_LABELS, TASK_LABELS, fmt, pct, statusOf, whenShort, words } from "../format";
import { capabilityLabel } from "../data";

const RESULT = (score) =>
  Number(score) >= 1 ? <Pill tone="good" dot>Did it</Pill> : Number(score) > 0 ? <Pill tone="warn" dot>Partly</Pill> : <Pill tone="bad" dot>Did not</Pill>;

/** Whether the agent did what people asked — judged turn by turn — and why the turns that went wrong did. */
export default function Quality() {
  const { params, set, f } = useFilters();
  const drill = (patch) => {
    set({ judged: "not_done", why: null, task: null, ...patch });
    requestAnimationFrame(() => document.getElementById(TURNS_CARD_ID)?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  const { data, loading, error } = useView("quality", params);
  const a = data?.averages ?? {};
  const outcomes = data?.outcomes ?? {};
  const judged = Object.values(outcomes).reduce((s, n) => s + n, 0);
  const openSession = (r) => r.sessionId && set({ session: r.sessionId });
  const sentiment = a.userSentiment == null ? "—" : a.userSentiment > 0.2 ? "Positive" : a.userSentiment < -0.2 ? "Negative" : "Neutral";

  const turnCols = [
    { key: "timestamp", label: "When", nowrap: true, render: (r) => whenShort(r.timestamp) },
    { key: "score", label: "Result", render: (r) => RESULT(r.score) },
    { key: "taskType", label: "What they wanted", render: (r) => TASK_LABELS[r.taskType] ?? words(r.taskType) },
    { key: "failureCategory", label: "Why", render: (r) => (r.failureCategory ? <Pill tone="neutral">{FAILURE_LABELS[r.failureCategory] ?? words(r.failureCategory)}</Pill> : "—") },
    { key: "reason", label: "Reviewer's note", max: 460, render: (r) => <span style={{ color: "var(--aa-ink-2)" }}>{r.reason}</span> },
  ];

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <PageHeading title="Quality">
        An AI reviewer reads each turn after it ends and judges it the way a person would.{" "}
        {judged ? `${fmt(judged)} turns reviewed in this period.` : "No turn has been reviewed in this period yet."}
      </PageHeading>

      <div className="aa-grid aa-grid-kpi aa-kpi-3">
        <Kpi loading={loading} onClick={() => drill({})} label="Did what people asked" info="Share of finished turns where the user got what they asked for (a partial counts half). Click to see every turn that fell short, with the reviewer's reason." value={pct(a.taskOutcome)} status={statusOf(a.taskOutcome, { good: 0.85, bad: 0.6 })} foot="good at 85%+" />
        <Kpi loading={loading} label="Kept every criterion" info="Of the criteria users stated in a search, the share the agent applied or openly said it could not. A criterion silently dropped counts against it." value={pct(a.criteriaFidelity)} status={statusOf(a.criteriaFidelity, { good: 0.9, bad: 0.7 })} foot="industry, location, size, titles…" />
        <Kpi loading={loading} label="Results matched the request" value={pct(a.resultRelevance)} status={statusOf(a.resultRelevance, { good: 0.8, bad: 0.6 })} foot="checked nightly on a sample" />
        <Kpi loading={loading} label="Ran without errors" value={pct(a.flowHealth)} status={statusOf(a.flowHealth, { good: 0.95, bad: 0.85 })} foot="good at 95%+" />
        <Kpi loading={loading} label="Stuck moments per turn" value={a.friction == null ? "—" : fmt(a.friction, 2)} status={statusOf(a.friction, { good: 0.1, bad: 0.3, higherIsBetter: false })} foot="0 is best" />
        <Kpi loading={loading} label="How users felt" info="Read from what users typed. Picking options says nothing about feelings, so it reads as neutral." value={sentiment} foot="from the words they typed" />
      </div>

      <div className="aa-grid aa-grid-2">
        <Card title="How turns ended" subtitle="Every reviewed turn">
          {loading ? (
            <RowsSkeleton rows={4} />
          ) : judged ? (
            <DonutBreakdown
              center={fmt(judged)}
              sub="turns"
              segments={OUTCOMES.filter((k) => outcomes[k.key]).map((k) => ({
                label: k.label,
                value: outcomes[k.key],
                color: k.color,
                help: k.help,
                ...(DRILLABLE.has(k.key) ? { onClick: () => drill({ judged: k.key }) } : {}),
              }))}
            />
          ) : (
            <Empty compact icon="quality" title="No reviewed turns yet" />
          )}
        </Card>
        <Card title="Why turns went wrong" subtitle="The reviewer's category for each failed or partial turn">
          {Object.keys(data?.failures ?? {}).length ? (
            <BarList
              color="var(--aa-bad)"
              rows={Object.entries(data.failures)
                .sort((x, y) => y[1] - x[1])
                .map(([k, v]) => ({ key: k, label: FAILURE_LABELS[k] ?? words(k), value: v, onClick: () => drill({ why: k }) }))}
            />
          ) : (
            <Empty compact icon="checkCircle" title={judged ? "Nothing went wrong" : "No reviewed turns yet"} />
          )}
        </Card>
      </div>

      <ProblemsCard params={params} />

      <Card title="Success by what people wanted" subtitle="Only turns that reached a result count; one waiting on the user is left out" flush>
        {loading ? (
          <RowsSkeleton rows={4} />
        ) : error ? (
          <ErrorState error={error} />
        ) : data?.perTask?.length ? (
          <Table
            defaultSort={{ key: "total", dir: "desc" }}
            columns={[
              { key: "task", label: "What they wanted", render: (r) => <span style={{ fontWeight: 500 }}>{TASK_LABELS[r.task] ?? words(r.task)}</span> },
              {
                key: "total",
                label: "Split",
                width: 220,
                sort: (r) => (r.success ?? 0) + (r.partial ?? 0) + (r.failure ?? 0) + (r.in_progress ?? 0),
                render: (r) => (
                  <SegmentBar
                    segments={[
                      { label: "Did it", value: r.success ?? 0, color: "var(--aa-good)" },
                      { label: "Partly", value: r.partial ?? 0, color: "var(--aa-warn)" },
                      { label: "Did not", value: r.failure ?? 0, color: "var(--aa-bad)" },
                      { label: "Waiting on the user", value: r.in_progress ?? 0, color: "var(--aa-neutral)" },
                    ]}
                  />
                ),
              },
              { key: "success", label: "Did it", align: "right", render: (r) => fmt(r.success ?? 0) },
              { key: "partial", label: "Partly", align: "right", render: (r) => fmt(r.partial ?? 0) },
              { key: "failure", label: "Did not", align: "right", render: (r) => fmt(r.failure ?? 0) },
              { key: "in_progress", label: "Waiting", align: "right", render: (r) => fmt(r.in_progress ?? 0) },
              { key: "successRate", label: "Success", align: "right", render: (r) => <Rate value={r.successRate} status={statusOf(r.successRate, { good: 0.85, bad: 0.6 })} /> },
            ]}
            rows={data.perTask.map((r) => ({ ...r, id: r.task }))}
            onRow={(r) => drill({ task: r.task })}
          />
        ) : (
          <Empty compact title="No reviewed turns in this period" />
        )}
      </Card>

      <TurnsCard params={params} f={f} set={set} drill={drill} />

      <Card title="Searches whose results did not fit the request" subtitle="Checked nightly on a sample · click a row to read it" flush>
        {data?.irrelevantResults?.length ? (
          <Table
            columns={turnCols.filter((col) => !["taskType", "failureCategory", "score"].includes(col.key))}
            rows={data.irrelevantResults.map((r, i) => ({ ...r, id: `${r.sessionId}-${i}` }))}
            onRow={openSession}
          />
        ) : (
          <Empty compact icon="search" title="None in this period" />
        )}
      </Card>
    </div>
  );
}

/** The outcomes a reviewed turn can have — the ones a click can list. Waiting and not-a-task turns get no verdict. */
const DRILLABLE = new Set(["success", "partial", "failure"]);

const JUDGED_WORDS = {
  not_done: "fell short",
  partial: "did part of it",
  failure: "did not do it",
  success: "did what they asked",
};

const TURN_PAGE = 50;

/** Where every clickable Quality figure lands: the turns card, scrolled to and filtered. */
const TURNS_CARD_ID = "quality-turns";

/**
 * "Turns that went wrong or only partly worked" — the ONE list of reviewed
 * turns on this page. Unfiltered it shows every turn that fell short; a click
 * on any figure above (the KPI, a "How turns ended" segment, a "Why turns went
 * wrong" bar, a task row) filters it in place and scrolls here. What the set
 * has in common is shown as chips (each a click to narrow), then each turn
 * with the reviewer's note; a turn opens its conversation.
 */
function TurnsCard({ params, f, set, drill }) {
  const [limit, setLimit] = useState(TURN_PAGE);
  const judged = f.judged || "not_done";
  const filtered = Boolean(f.judged || f.why || f.task);
  const query = { judged, ...(f.why ? { why: f.why } : {}), ...(f.task ? { task: f.task } : {}), limit };
  const { data, loading, error } = useView("turns", params, query);
  const sum = data?.summary;
  const close = () => set({ judged: null, why: null, task: null });
  const title = filtered
    ? [
        `Turns that ${JUDGED_WORDS[judged] ?? words(judged)}`,
        f.why ? `because: ${FAILURE_LABELS[f.why] ?? words(f.why)}` : null,
        f.task ? `wanting: ${TASK_LABELS[f.task] ?? words(f.task)}` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "Turns that went wrong or only partly worked";

  return (
    <div id={TURNS_CARD_ID} style={{ scrollMarginTop: 16 }}>
    <Card
      title={data ? `${title} · ${fmt(data.total)}` : title}
      subtitle={sum && judged === "not_done" ? `${fmt(sum.partial)} did part of it · ${fmt(sum.failure)} did not do it · click a reason or a task to narrow, a turn to read it` : "Click a reason or a task to narrow, a turn to read it"}
      actions={
        <span style={{ display: "inline-flex", gap: 8 }}>
          <button type="button" className="aa-btn aa-btn-sm" onClick={() => set({ tab: "conversations", "c.judged": judged, "c.why": f.why ?? null })}>
            Open these conversations
          </button>
          {filtered && (
            <button type="button" className="aa-btn aa-btn-sm" onClick={close}>
              Clear filters
            </button>
          )}
        </span>
      }
    >
      {loading ? (
        <RowsSkeleton rows={6} />
      ) : error ? (
        <ErrorState error={error} />
      ) : data?.total ? (
        <div style={{ display: "grid", gap: 14 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <span className="aa-sub" style={{ marginRight: 4 }}>Why</span>
            {sum.byCategory.map((c) => (
              <span key={c.key} style={{ cursor: c.key === "none" ? "default" : "pointer" }} onClick={() => c.key !== "none" && drill({ judged: f.judged, task: f.task, why: f.why === c.key ? null : c.key })}>
                <Pill tone={f.why === c.key ? "bad" : "neutral"}>
                  {c.key === "none" ? "No category" : FAILURE_LABELS[c.key] ?? words(c.key)} · {fmt(c.count)}
                </Pill>
              </span>
            ))}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <span className="aa-sub" style={{ marginRight: 4 }}>Wanted</span>
            {sum.byTask.map((c) => (
              <span key={c.key} style={{ cursor: c.key === "none" ? "default" : "pointer" }} onClick={() => c.key !== "none" && drill({ judged: f.judged, why: f.why, task: f.task === c.key ? null : c.key })}>
                <Pill tone={f.task === c.key ? "warn" : "neutral"}>
                  {c.key === "none" ? "Unknown" : TASK_LABELS[c.key] ?? words(c.key)} · {fmt(c.count)}
                </Pill>
              </span>
            ))}
          </div>
          <div className="aa-card" style={{ boxShadow: "none", overflow: "hidden" }}>
            <Table
              columns={[
                { key: "timestamp", label: "When", nowrap: true, render: (r) => whenShort(r.timestamp) },
                { key: "score", label: "Result", render: (r) => RESULT(r.score) },
                { key: "conversation", label: "Conversation", max: 240, render: (r) => <span style={{ color: "var(--aa-ink-2)" }}>{r.conversationTitle || r.firstPrompt || "—"}</span> },
                { key: "taskType", label: "What they wanted", render: (r) => TASK_LABELS[r.taskType] ?? words(r.taskType) },
                { key: "failureCategory", label: "Why", render: (r) => (r.failureCategory ? <Pill tone="neutral">{FAILURE_LABELS[r.failureCategory] ?? words(r.failureCategory)}</Pill> : "—") },
                { key: "reason", label: "Reviewer's note", max: 460, render: (r) => <span style={{ color: "var(--aa-ink-2)" }}>{r.reason}</span> },
              ]}
              rows={data.turns.map((r, i) => ({ ...r, id: `${r.sessionId}-${i}` }))}
              onRow={(r) => r.sessionId && set({ session: r.sessionId })}
            />
          </div>
          {data.turns.length < data.total && (
            <div>
              <button type="button" className="aa-btn aa-btn-sm" onClick={() => setLimit((n) => n + TURN_PAGE)}>
                Show more ({fmt(data.total - data.turns.length)} left)
              </button>
            </div>
          )}
          {data.capped && <div className="aa-sub">Showing the newest reviewed turns only — narrow the date range to see older ones.</div>}
        </div>
      ) : (
        <Empty compact icon="checkCircle" title="No reviewed turns match" />
      )}
    </Card>
    </div>
  );
}

/** The conversations one problem detail row happened in; a click opens one. */
function ProblemConversations({ params, code, details, onOpen }) {
  const extra = { code, ...Object.fromEntries(Object.entries(details).filter(([, v]) => v != null).map(([k, v]) => [`d.${k}`, v])) };
  const { data, loading, error } = useView("problem-conversations", params, extra);
  const rows = data?.conversations ?? [];
  const heading = (
    <div style={{ fontSize: 13 }}>
      <span style={{ fontWeight: 600 }}>Conversations where this happened</span>
      <span className="aa-sub"> · {detailWords(details)}</span>
    </div>
  );
  if (loading) return <RowsSkeleton rows={3} />;
  if (error) return <ErrorState error={error} />;
  if (!rows.length) return <div style={{ display: "grid", gap: 6 }}>{heading}<Empty compact title="Not linked to a conversation" /></div>;
  return (
    <div style={{ display: "grid", gap: 6 }}>
    {heading}
    <div className="aa-card" style={{ boxShadow: "none", overflow: "hidden" }}>
      <Table
        columns={[
          { key: "title", label: "Conversation", max: 320, render: (r) => (r.sessionId ? r.title || r.firstPrompt || r.sessionId : <span style={{ color: "var(--aa-faint)" }}>Before any conversation (refused at the door)</span>) },
          { key: "lastActivityAt", label: "Last active", nowrap: true, render: (r) => whenShort(r.lastActivityAt) },
          { key: "count", label: "Times", align: "right", render: (r) => fmt(r.count) },
        ]}
        rows={rows.map((r, i) => ({ ...r, id: r.sessionId ?? `none-${i}` }))}
        onRow={(r) => r.sessionId && onOpen(r.sessionId)}
      />
    </div>
    </div>
  );
}

/** A detail row in words: `Location · Asked · "nyc metro"` — the user's own phrase quoted, the rest named. */
function detailWords(details) {
  return Object.entries(details)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => (RAW_DETAILS.has(k) ? `“${v}”` : words(v)))
    .join(" · ");
}

/** Detail values printed as written: the user's own phrase, an intake issue code. */
const RAW_DETAILS = new Set(["item", "issue"]);

/**
 * What the agent handled without failing the turn (`/obs/v1/problems`): each
 * problem by how often it happened, and for the one picked, where it happened
 * and its most common details — the part that says what to fix.
 */
function ProblemsCard({ params }) {
  const { set } = useFilters();
  const { data, loading, error } = useView("problems", params);
  const codes = data?.codes ?? [];
  const [picked, setPicked] = useState(null);
  const [detail, setDetail] = useState(null);
  const pick = (code) => {
    setPicked(code);
    setDetail(null);
  };
  const current = codes.find((c) => c.code === picked) ?? codes[0];
  const info = current ? PROBLEM_LABELS[current.code] : null;
  const keys = current?.top?.[0] ? Object.keys(current.top[0].details) : [];

  return (
    <Card
      title="Problems the agent handled"
      subtitle={data ? `${fmt(data.total ?? 0)} in this period · things that went wrong without failing the turn · click one for its details` : "Things that went wrong without failing the turn"}
    >
      {loading ? (
        <RowsSkeleton rows={5} />
      ) : error ? (
        <ErrorState error={error} />
      ) : codes.length ? (
        <div className="aa-grid aa-grid-2" style={{ alignItems: "start" }}>
          <BarList
            color="var(--aa-s3)"
            stacked
            selectedKey={current?.code}
            rows={codes.map((c) => ({
              key: c.code,
              label: PROBLEM_LABELS[c.code]?.label ?? words(c.code),
              title: PROBLEM_LABELS[c.code]?.help,
              value: c.count,
              onClick: () => pick(c.code),
            }))}
          />
          {current && (
            <div style={{ display: "grid", gap: 10 }}>
              <div>
                <div style={{ fontWeight: 600 }}>{info?.label ?? words(current.code)}</div>
                <div className="aa-sub" style={{ marginTop: 2 }}>
                  <span className="aa-mono">{current.code}</span>
                  {info?.help ? ` · ${info.help}` : ""}
                </div>
              </div>
              {current.capabilities.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {current.capabilities.map((c) => (
                    <Pill key={c.capability} tone="neutral">
                      {c.capability === "none" ? "Outside any capability" : capabilityLabel(c.capability)} · {fmt(c.count)}
                    </Pill>
                  ))}
                </div>
              )}
              {current.top.length ? (
                <div className="aa-card" style={{ boxShadow: "none", overflow: "hidden" }}>
                  <Table
                    columns={[
                      ...keys.map((k) => ({
                        key: k,
                        label: words(k),
                        sort: (r) => r.details[k],
                        render: (r) =>
                          r.details[k] == null ? "—" : RAW_DETAILS.has(k) ? <span className="aa-mono">{r.details[k]}</span> : words(r.details[k]),
                      })),
                      { key: "count", label: "Times", align: "right", render: (r) => fmt(r.count) },
                    ]}
                    rows={current.top.map((r, i) => ({ ...r, id: i }))}
                    selectedKey={detail}
                    onRow={(r) => setDetail(detail === r.id ? null : r.id)}
                  />
                </div>
              ) : (
                <Empty compact title="No details recorded" />
              )}
              {current.top.length > 0 && detail == null && <div className="aa-sub">Click a row to see the conversations it happened in.</div>}
              {detail != null && current.top[detail] && (
                <ProblemConversations params={params} code={current.code} details={current.top[detail].details} onOpen={(id) => set({ session: id })} />
              )}
            </div>
          )}
        </div>
      ) : (
        <Empty compact icon="checkCircle" title="No problems recorded in this period" />
      )}
    </Card>
  );
}
